/**
 * Console da plataforma: o mesmo build servido em `console.<host do app>`.
 *
 *   7days.digital          ↔ console.7days.digital
 *   staging.7days.digital  ↔ console.staging.7days.digital
 *   localhost:5173         ↔ console.localhost:5173
 *
 * O host decide no arranque qual árvore sobe (ver src/vite/main.tsx). No console não há
 * organização ativa: a URL não leva slug e a marca é sempre a da 7Days.
 */
const CONSOLE_PREFIX = "console.";

type HostLocation = Pick<Location, "protocol" | "hostname" | "port">;

export function isConsoleHost(hostname: string = typeof window === "undefined" ? "" : window.location.hostname): boolean {
    return hostname.toLowerCase().startsWith(CONSOLE_PREFIX);
}

function originOf({ protocol, port }: HostLocation, hostname: string): string {
    return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
}

/** Origem do console a partir do app (ou o próprio console, se já estiver nele). */
export function consoleOrigin(location: HostLocation = window.location): string {
    const host = isConsoleHost(location.hostname) ? location.hostname : `${CONSOLE_PREFIX}${location.hostname}`;
    return originOf(location, host);
}

/** Origem do app a partir do console (ou o próprio app, se já estiver nele). */
export function appOrigin(location: HostLocation = window.location): string {
    const host = isConsoleHost(location.hostname) ? location.hostname.slice(CONSOLE_PREFIX.length) : location.hostname;
    return originOf(location, host);
}

/**
 * Liga a passagem do app para o console (link na sidebar e redirecionamento de /platform).
 * Fica desligada até o host do console existir no ambiente: sem DNS, o app perderia a plataforma.
 */
export function isConsoleEnabled(): boolean {
    return process.env.NEXT_PUBLIC_CONSOLE_ENABLED === "true";
}

/** "/platform/tenants/x?tab=y" → "<console>/tenants/x?tab=y"; null fora de /platform. */
export function consoleUrlForPlatformPath(path: string, location: HostLocation = window.location): string | null {
    const match = /^\/platform(?=\/|\?|#|$)(.*)$/.exec(path);
    if (!match) return null;
    const rest = match[1] ?? "";
    return `${consoleOrigin(location)}${rest.startsWith("/") ? rest : `/${rest}`}`;
}
